import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { getSettings } from "@/lib/api";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Content() {
  const [videoId, setVideoId] = useState("mm-GvvQBLgc");
  const [title, setTitle] = useState("LATEST TRANSMISSION");

  useEffect(() => {
    getSettings()
      .then((s) => {
        if (s?.featured_video_id) setVideoId(s.featured_video_id);
        if (s?.featured_title) setTitle(s.featured_title);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="content" className="relative overflow-hidden bg-void px-6 py-28 md:px-10 md:py-36" data-testid="content">
      {/* ambient reactor glow */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-porthole-blue/5 blur-[120px]" />

      <div className="relative mx-auto max-w-[1300px]">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mb-12 flex flex-col items-center text-center"
        >
          <span className="font-mono text-xs tracking-[0.3em] text-porthole-blue md:text-[15px]">// INCOMING SIGNAL</span>
          <h2 className="mt-3 font-display text-5xl uppercase leading-none text-white md:text-7xl">
            Transmission Feed
          </h2>
        </motion.div>

        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="relative mx-auto max-w-5xl"
        >
          {/* Monitor frame */}
          <div className="relative border-2 border-concrete bg-gunmetal p-3 shadow-[0_0_60px_rgba(0,240,255,0.12)] md:p-5">
            {/* top bezel bar */}
            <div className="mb-3 flex items-center justify-between border-b border-white/10 px-1 pb-2 font-mono text-[10px] tracking-[0.25em] text-white/50 md:text-[13px]">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 animate-pulse bg-reactor-red" />
                <span className="text-reactor-red">REC</span>
                <span className="ml-3 hidden sm:inline">CH-78 // {title}</span>
              </div>
              <span className="hidden sm:inline text-tactical-green">SIGNAL LOCKED</span>
            </div>

            {/* Screen */}
            <div className="relative aspect-video w-full overflow-hidden bg-black animate-flicker">
              <iframe
                key={videoId}
                className="absolute inset-0 h-full w-full"
                src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                title="THINKAVARIUM latest episode"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="youtube-embed"
              />
              {/* scanline + inset glow overlay */}
              <div className="scanlines pointer-events-none absolute inset-0 z-10 opacity-40" />
              <div className="pointer-events-none absolute inset-0 z-10 shadow-[inset_0_0_60px_rgba(0,240,255,0.18)]" />
            </div>

            {/* bottom telemetry bar */}
            <div className="mt-3 grid grid-cols-3 gap-3 border-t border-white/10 px-1 pt-2 font-mono text-[10px] tracking-widest text-white/40 md:text-[13px]">
              <span>BITRATE 4.2Mbps</span>
              <span className="text-center text-hazard-amber">● LIVE ARCHIVE</span>
              <span className="text-right">1080P · 60FPS</span>
            </div>
          </div>

          <p className="mt-8 text-center font-body text-sm font-light text-white/50 md:text-[18px]">
            This feed rotates weekly. Enlist below so the next transmission reaches you directly.
          </p>
          <div className="mt-5 text-center">
            <a
              href={`https://youtu.be/${videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block border border-white/25 px-7 py-3 font-mono text-xs tracking-[0.3em] text-white/80 transition-colors duration-100 hover:border-reactor-red hover:text-reactor-red md:text-[15px]"
              data-testid="watch-on-youtube"
            >
              WATCH ON YOUTUBE ↗
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
